/**
 * One banner upload, two derived strips.
 *
 * Brands upload a single banner in Company details. The templated join page
 * shows it as a 5:1 strip on desktop and 2:1 on mobile, while a custom join
 * page gets the untouched original as `{{brand.hero}}`. The strips are
 * rendered client-side from the original: automatically (centre crop) on
 * upload, or from the areas the brand picked in Customizations › Banner
 * framing. Areas are stored as react-easy-crop percentages so the framing
 * modal can reopen exactly where the brand left it.
 */
import { blobToFile, createImage, getCroppedImg } from "./canvasUtils";
import { storageService } from "../services/storage.service";
import type { BannerCrop, BannerCropArea } from "../types/brand";

export const BANNER_DESKTOP_RATIO = 5; // 5:1
export const BANNER_MOBILE_RATIO = 2; // 2:1

export interface PixelArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Largest centred area of the given aspect ratio that fits the image. */
export function centreArea(
  imageWidth: number,
  imageHeight: number,
  ratio: number
): PixelArea {
  let width = imageWidth;
  let height = Math.round(width / ratio);
  if (height > imageHeight) {
    height = imageHeight;
    width = Math.round(height * ratio);
  }
  return {
    x: Math.round((imageWidth - width) / 2),
    y: Math.round((imageHeight - height) / 2),
    width,
    height,
  };
}

export function areaToPercent(area: PixelArea, w: number, h: number): BannerCropArea {
  return {
    x: (area.x / w) * 100,
    y: (area.y / h) * 100,
    width: (area.width / w) * 100,
    height: (area.height / h) * 100,
  };
}

export function percentToArea(area: BannerCropArea, w: number, h: number): PixelArea {
  const x = Math.max(0, Math.round((area.x / 100) * w));
  const y = Math.max(0, Math.round((area.y / 100) * h));
  return {
    x,
    y,
    width: Math.min(w - x, Math.round((area.width / 100) * w)),
    height: Math.min(h - y, Math.round((area.height / 100) * h)),
  };
}

function stripName(originalName: string, suffix: string) {
  const base = originalName.replace(/\.[^.]+$/, "") || "banner";
  return `${base}-${suffix}.jpg`;
}

export interface BannerStrips {
  desktop: File;
  mobile: File;
  /** The areas actually used, in percentages, for persisting. */
  crop: Required<BannerCrop>;
}

/**
 * Render the desktop and mobile strips from the original. Any side missing
 * from `crop` falls back to the centre crop for that ratio.
 */
export async function renderBannerStrips(
  originalSrc: string,
  fileName: string,
  crop?: BannerCrop | null
): Promise<BannerStrips> {
  const image = await createImage(originalSrc);
  const w = image.naturalWidth;
  const h = image.naturalHeight;

  const desktopArea = crop?.desktop
    ? percentToArea(crop.desktop, w, h)
    : centreArea(w, h, BANNER_DESKTOP_RATIO);
  const mobileArea = crop?.mobile
    ? percentToArea(crop.mobile, w, h)
    : centreArea(w, h, BANNER_MOBILE_RATIO);

  const [desktopBlob, mobileBlob] = await Promise.all([
    getCroppedImg(originalSrc, desktopArea),
    getCroppedImg(originalSrc, mobileArea),
  ]);

  return {
    desktop: blobToFile(desktopBlob, stripName(fileName, "desktop")),
    mobile: blobToFile(mobileBlob, stripName(fileName, "mobile")),
    crop: {
      desktop: areaToPercent(desktopArea, w, h),
      mobile: areaToPercent(mobileArea, w, h),
    },
  };
}

export interface UploadedBannerSet {
  brandBannerImageOriginal: string;
  brandBannerImage: string;
  brandBannerImageMobile: string;
}

/**
 * Upload a freshly chosen banner: the original plus centre-cropped strips.
 * Used by both the signup flow and Company details so neither path leaves
 * the join page with a stretched or missing strip.
 */
export async function uploadBannerSet(original: File): Promise<UploadedBannerSet> {
  const src = URL.createObjectURL(original);
  try {
    const strips = await renderBannerStrips(src, original.name);
    const [brandBannerImageOriginal, brandBannerImage, brandBannerImageMobile] =
      await Promise.all([
        storageService.uploadCompanyBanner(original),
        storageService.uploadCompanyBanner(strips.desktop),
        storageService.uploadCompanyBannerMobile(strips.mobile),
      ]);
    return { brandBannerImageOriginal, brandBannerImage, brandBannerImageMobile };
  } finally {
    URL.revokeObjectURL(src);
  }
}
